/**
 * Eine Klasse zur Erstellung von HTML Elementen.
 *
 * @remarks
 * Diese Klasse implementiert eine Vielzahl üblicher Methoden, welche von jedem
 * HTML Element verwendet werden. Zum Beispiel `withId`, `withClass`,
 * `withEventListener`, etc.
 *
 * Zudem unterstützt die Klasse Method Chaining. Die Konfiguration von HTML
 * Elementen kann daher in einem Schritt erfolgen.
 *
 * _Beachte_: Es werden nicht für alle verfügbaren Eigenschaften der
 * `HTMLElement` Klasse entsprechende Methoden implementiert.
 *
 * @example
 * Das folgende Beispiel demonstriert, wie die abstrakte Basisklasse erweitert
 * werden kann, um neue HTML Elemente zu erstellen.
 *
 * ```typescript
 * // Erstellung von einem HTML `<form>` Element.
 * class Form extends Component<HTMLFormElement> {
 *      constructor() {
 *          super(document.createElement("form"));
 *      }
 * }
 * ```
 *
 * @example
 * Das folgende Beispiel demonstriert, wie ein HTML Element durch
 * Method Chaining konfiguriert werden kann.
 *
 * Erstelle ein neues HTML `<form>` Element.
 *
 * ```typescript
 * const form = new Form();
 * ```
 *
 * Konfiguriere das HTML `<form>` Element durch Method Chaining.
 *
 * ```typescript
 * form
 *     .withID("search")
 *     .withClass("search")
 *     .withEventListener("submit", (event) => {
 *         // Verhindere die serverseitige Übertragung.
 *         event.preventDefault();
 *     });
 * ```
 *
 * @example
 * Das folgende Beispiel demonstriert, wie ein erstelltes HTML Element im DOM
 * dargestellt werden kann.
 *
 * Erstelle ein neues HTML `<form>` Element.
 *
 * ```typescript
 * const form = new Form();
 * ```
 *
 * Rendere das HTML Element in das DOM.
 *
 * ```typescript
 * document.getElementById("root")
 *     ?.replaceChildren(form.render());
 * ```
 *
 * @public
 */
abstract class Component<E extends HTMLElement> {
  /**
   * Das HTML Element.
   */
  protected element: E;

  /**
   * Erstellt ein neues HTML Element.
   *
   * @param element - Ein HTML Element.
   */
  constructor(element: E) {
    this.element = element;
  }

  /**
   * Fügt eine `id` hinzu.
   *
   * @param id - Der Wert von einem `id`-Attribut.
   * @returns Das HTML Element mit der `id`.
   *
   * @public
   */
  public withID(id: HTMLElement["id"]) {
    this.element.id = id;

    return this;
  }

  /**
   * Fügt untergeordnete HTML Elemente hinzu.
   *
   * @param children - Eine oder mehrere HTML Elemente.
   * @returns Das HTML Element mit den untergeordneten HTML Elementen.
   *
   * @public
   */
  public withChildren(...children: Array<Component<HTMLElement>>) {
    this.element.append(...children.map((child) => child.render()));

    return this;
  }

  /**
   * Fügt Text hinzu.
   *
   * @param text - Ein Text.
   * @returns Das HTML Element mit dem Text.
   *
   * @public
   */
  public withText(text: HTMLElement["innerText"]) {
    this.element.innerText = text;

    return this;
  }

  /**
   * Fügt HTML Markup hinzu.
   *
   * @param html - Ein HTML Markup.
   * @returns Das HTML Element mit dem HTML Markup.
   *
   * @public
   */
  public withHTML(html: HTMLElement["innerHTML"]) {
    this.element.innerHTML = html;

    return this;
  }

  /**
   * Fügt einen EventListener für ein Event hinzu.
   *
   * @param event - Ein Event.
   * @param handler - Ein Eventhandler.
   * @returns Das HTML Element mit dem EventListener.
   *
   * @public
   */
  withEventListener<K extends keyof HTMLElementEventMap>(
    event: K,
    handler: (this: E, event: HTMLElementEventMap[K] | Event) => unknown
  ) {
    this.element.addEventListener(event, handler);

    return this;
  }

  /**
   * Fügt CSS Klassen hinzu.
   *
   * @param classes - Eine oder mehrere CSS Klassen.
   * @returns Das HTML Element mit den CSS Klassen.
   *
   * @public
   */
  public withClass(...classes: Array<string>) {
    this.element.classList.add(...classes);

    return this;
  }

  /**
   * Entfernt CSS Klassen.
   *
   * @param classes - Eine oder mehrere CSS Klassen.
   * @returns Das HTML Element ohne die CSS Klassen.
   *
   * @public
   */
  public removeClass(...classes: Array<string>) {
    this.element.classList.remove(...classes);

    return this;
  }

  /**
   * Blendet das HTML Element aus.
   *
   * @returns Das ausgeblendete HTML Element.
   *
   * @public
   */
  public hide() {
    this.withClass("hide");

    return this;
  }

  /**
   * Blendet das HTML Element ein.
   *
   * @returns Das eingeblendete HTML Element.
   *
   * @public
   */
  public show() {
    this.removeClass("hide");

    return this;
  }

  /**
   * Gibt das HTML Element mit den konfigurierten Eigenschaften zurück.
   *
   * @returns Das HTML Element mit den konfigurierten Eigenschaften.
   *
   * @public
   * @virtual
   */
  public render() {
    return this.element;
  }
}

export { Component };
